import React, { useState } from 'react'
import { useCallback } from 'react'
import VerticalGroup from '../../atoms/verticalgroup/VerticalGroup'
import ModelAPI from '../../../services/modelApi'
import LoadingSpinner from '../../atoms/loadingspinner/LoadingSpinner'
import Banner from '../../atoms/banner/Banner'

import InlineGroup from '../../atoms/inlinegroup/InlineGroup'
import IconButton from '../../molecules/iconbutton/IconButton'
import { wait } from '../../../lib/wait'
import openNewTab from '../../../lib/openNewTab'
interface Props {
  eventId: string
  selectedRound: number
  data: any
  refresh: () => void
  error: Error | null
  inProgress: boolean
  children: React.ReactNode
}

const ModelPageContainer: React.FC<Props> = ({ eventId, selectedRound, data, refresh, error, inProgress, children }) => {
  const [recalculateError, setRecalculateError] = useState(null)
  const [calcInProgress, setCalcInProgress] = useState<'calc' | 'sculpt' | 'reporting' | 'upload-results' | null>(null)

  const onRecalculateAndSculptAll = useCallback(async () => {
    setRecalculateError(null)
    setCalcInProgress('calc')
    try {
      await ModelAPI.recalculateAndSculptAllResults(eventId, selectedRound)
    } catch (e) {
      setRecalculateError((e as any).message)
    }
    setCalcInProgress(null)
    refresh()
  }, [eventId, selectedRound, refresh])

  const onRecalculateAll = useCallback(async () => {
    setRecalculateError(null)
    setCalcInProgress('calc')
    try {
      await ModelAPI.recalculateAllResults(eventId, selectedRound)
    } catch (e) {
      setRecalculateError((e as any).message)
    }
    setCalcInProgress(null)
    refresh()
  }, [eventId, selectedRound, refresh])

  const onRecalculate = useCallback(async () => {
    setRecalculateError(null)
    setCalcInProgress('calc')
    try {
      await ModelAPI.recalculateResults(eventId, selectedRound)
    } catch (e) {
      setRecalculateError((e as any).message)
    }
    setCalcInProgress(null)
    refresh()
  }, [eventId, selectedRound, refresh])

  const onSculpt = useCallback(async () => {
    setRecalculateError(null)
    setCalcInProgress('sculpt')
    try {
      await ModelAPI.sculptResults(eventId, selectedRound)
      let iteration = 0
      while (true) {
        iteration++
        const configuration = await ModelAPI.getModelConfiguration(eventId)
        if (configuration.state === 'round-sculpted') {
          break
        }
        if (iteration > 100) {
          throw new Error('Timed out sculpting')
        }
        await wait(5000)
      }
    } catch (e) {
      setRecalculateError((e as any).message)
    }
    setCalcInProgress(null)
    refresh()
  }, [eventId, selectedRound, refresh])

  const onRecalculateReporting = useCallback(async () => {
    setRecalculateError(null)
    setCalcInProgress('reporting')
    try {
      await ModelAPI.recalculateReporting(eventId, selectedRound)
    } catch (e) {
      setRecalculateError((e as any).message)
    }
    setCalcInProgress(null)
    refresh()
  }, [eventId, selectedRound, refresh])

  const onUploadResults = useCallback(async () => {
    setCalcInProgress('upload-results')

    try {
      await ModelAPI.uploadResults(eventId, selectedRound + 1)
    } catch (e) {
      setRecalculateError((e as any).message)
    }
    setCalcInProgress(null)
  }, [eventId, selectedRound])

  const onRefresh = useCallback(async () => {
    refresh()
  }, [refresh])

  return (
    <VerticalGroup spaceBetweenElements={2} wide>
      <InlineGroup spaceLeft={2} right block>
        {process.env.REACT_APP_CALC_ALL_ROUND_ENABLED && <IconButton icon='calculator' text='Recalculate & Sculpt All Rounds' onClick={onRecalculateAndSculptAll} inProgress={calcInProgress === 'calc'} />}
        {process.env.REACT_APP_CALC_ALL_ROUND_ENABLED && <IconButton icon='calculator' text='Recalculate All Rounds' onClick={onRecalculateAll} inProgress={calcInProgress === 'calc'} />}
        <IconButton icon='calculator' text='Recalculate' onClick={onRecalculate} inProgress={calcInProgress === 'calc'} />
        <IconButton icon='sculpting' text='Sculpt' onClick={onSculpt} inProgress={calcInProgress === 'sculpt'} />
        <IconButton icon='calculator' text='Calculate Reporting' onClick={onRecalculateReporting} inProgress={calcInProgress === 'reporting'}/>
        <IconButton icon='upload' text='Upload Results' onClick={onUploadResults} inProgress={calcInProgress === 'upload-results'} />
        <IconButton icon='eye' onClick={() => openNewTab(`${window.location.origin}/results/games/${eventId}/rounds/${selectedRound}`)} text='View Results'/>
        <IconButton icon='refresh' text='Refresh' onClick={onRefresh} inProgress={inProgress} />
      </InlineGroup>
      {(!!error || !!recalculateError) && <Banner type='error' active message={error?.message ?? recalculateError ?? ''}/>}
      {inProgress && !data && <LoadingSpinner />}
      {!error && !recalculateError && !!data && children}
    </VerticalGroup>
  )
}

export default ModelPageContainer